import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronRight, X, Eye } from 'lucide-react';
import type { FlowQuestion } from '../../../types';
import { STATUS } from '../../../styles/tokens';

// ── Message Types ─────────────────────────────────────────────────────────────

export type ChatMessage =
  | { id: string; type: 'user'; content: string }
  | { id: string; type: 'assistant-text'; content: string }
  | {
      id: string;
      type: 'event-settingChange';
      changes: Array<{ field: string; oldValue: string; value: string }>;
      confirmed: boolean;
      cancelled: boolean;
    }
  | { id: string; type: 'event-confirmGenerate'; visualModifications: string; confirmed: boolean }
  | {
      id: string;
      type: 'event-askQuestions';
      questions: Array<{ title: string; options: string[] }>;
      answered: boolean;
    };

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveFieldLabel(questions: FlowQuestion[] | undefined, field: string): string {
  return questions?.find(q => q.id === field)?.title ?? field;
}

function resolveOptionLabel(questions: FlowQuestion[] | undefined, field: string, value: string): string {
  const q = questions?.find(q => q.id === field);
  return q?.options?.find(o => o.value === value)?.label ?? value;
}

function displayValue(questions: FlowQuestion[] | undefined, field: string, value: string, empty = '（未設定）'): string {
  if (!value) return empty;
  if (field === 'content') return value.length > 40 ? value.slice(0, 40) + '...' : value;
  return resolveOptionLabel(questions, field, value);
}

// ── SettingChangeCard ─────────────────────────────────────────────────────────

interface SettingChangeCardProps {
  msg: Extract<ChatMessage, { type: 'event-settingChange' }>;
  onApply: (id: string) => void;
  onCancel: (id: string) => void;
  questions?: FlowQuestion[];
}

export const SettingChangeCard: React.FC<SettingChangeCardProps> = ({ msg, onApply, onCancel, questions }) => {
  const [showDiff, setShowDiff] = useState(false);

  if (msg.confirmed) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 ${STATUS.success} rounded-xl text-xs`}>
        <Check size={13} />
        已確認 {msg.changes.length} 項修改：{msg.changes.map(c => resolveFieldLabel(questions, c.field)).join('、')}
      </div>
    );
  }

  if (msg.cancelled) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-400 dark:text-slate-500">
        <X size={13} />
        已取消修改建議（{msg.changes.map(c => resolveFieldLabel(questions, c.field)).join('、')}）
      </div>
    );
  }

  return (
    <>
      <div className={`${STATUS.warning} rounded-xl px-3.5 py-3 space-y-3 max-w-[90%]`}>
        <p className="text-xs font-bold text-amber-600 dark:text-amber-400">
          AI 建議修改 {msg.changes.length} 項設定
        </p>
        <div className="space-y-1">
          {msg.changes.map((c, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <span className="font-medium text-slate-600 dark:text-slate-300 shrink-0">
                {resolveFieldLabel(questions, c.field)}
              </span>
              <ChevronRight size={11} className="shrink-0 text-amber-400" />
              <span className="text-slate-700 dark:text-slate-200 font-medium truncate">
                {displayValue(questions, c.field, c.value)}
              </span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowDiff(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 rounded-lg text-xs font-medium hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-all"
          >
            <Eye size={12} /> 查看修改建議
          </button>
          <button
            onClick={() => onApply(msg.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-400 transition-all"
          >
            <Check size={12} /> 確認修改
          </button>
          <button
            onClick={() => onCancel(msg.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 rounded-lg text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-all"
          >
            <X size={12} /> 取消修改
          </button>
        </div>
      </div>

      {showDiff && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => setShowDiff(false)}
        >
          <div
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-5 w-[90vw] max-w-md mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-sm text-slate-800 dark:text-slate-100">修改建議前後對比</p>
              <button
                onClick={() => setShowDiff(false)}
                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 transition-colors"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3 mb-5">
              {msg.changes.map((c, i) => (
                <div key={i} className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3 space-y-2">
                  <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                    {resolveFieldLabel(questions, c.field)}
                  </p>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-slate-400 mb-0.5">修改前</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 line-through break-words">
                        {displayValue(questions, c.field, c.oldValue, '（未設定）')}
                      </p>
                    </div>
                    <ChevronRight size={14} className="text-amber-400 shrink-0 mt-3" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-amber-500 mb-0.5">修改後</p>
                      <p className="text-xs text-slate-700 dark:text-slate-200 font-medium break-words">
                        {displayValue(questions, c.field, c.value)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { onCancel(msg.id); setShowDiff(false); }}
                className="px-4 py-2 rounded-xl text-sm font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
              >
                取消修改
              </button>
              <button
                onClick={() => { onApply(msg.id); setShowDiff(false); }}
                className="px-4 py-2 rounded-xl text-sm font-bold text-white bg-amber-500 hover:bg-amber-400 transition-all"
              >
                確認修改
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// ── ConfirmGenerateCard ───────────────────────────────────────────────────────

interface ConfirmGenerateCardProps {
  msg: Extract<ChatMessage, { type: 'event-confirmGenerate' }>;
  isGenerating: boolean;
}

export const ConfirmGenerateCard: React.FC<ConfirmGenerateCardProps> = ({ isGenerating }) => {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl text-xs text-emerald-600 dark:text-emerald-400">
      {isGenerating ? (
        <>
          <div className="w-3 h-3 border-2 border-emerald-400/40 border-t-emerald-500 rounded-full animate-spin shrink-0" />
          開始生成中...
        </>
      ) : (
        <>
          <Check size={13} /> 已確認生成
        </>
      )}
    </div>
  );
};

// ── AskQuestionsCard ──────────────────────────────────────────────────────────

interface AskQuestionsCardProps {
  msg: Extract<ChatMessage, { type: 'event-askQuestions' }>;
  onSubmit: (msgId: string, answers: string) => void;
  isTyping: boolean;
}

export const AskQuestionsCard: React.FC<AskQuestionsCardProps> = ({ msg, onSubmit, isTyping }) => {
  const [selections, setSelections] = useState<Record<number, string>>({});
  const [customInputOpen, setCustomInputOpen] = useState<Record<number, boolean>>({});

  if (msg.answered) {
    return (
      <div className="px-3 py-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-400 dark:text-slate-500 italic">
        已回覆問題
      </div>
    );
  }

  const handleSubmit = () => {
    const lines: string[] = [];
    msg.questions.forEach((q, i) => {
      const sel = selections[i];
      if (sel) lines.push(`針對問題[${q.title}]：${sel}`);
    });
    const combined = lines.join('\\n');
    onSubmit(msg.id, combined);
  };

  const canSubmit = msg.questions.every((_, i) => !!selections[i]);

  return (
    <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-3 space-y-3 max-w-[90%]">
      {msg.questions.map((q, qi) => {
        const isCustom = customInputOpen[qi] || false;
        const isPresetSelected = !isCustom && q.options.includes(selections[qi]);
        return (
          <div key={qi} className="space-y-1.5">
            <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{q.title}</p>
            <div className="flex flex-wrap gap-1.5">
              {q.options.map((opt, oi) => {
                const isSelected = isPresetSelected && selections[qi] === opt;
                return (
                  <button
                    key={oi}
                    onClick={() => {
                      setCustomInputOpen(prev => ({ ...prev, [qi]: false }));
                      setSelections(s => ({ ...s, [qi]: opt }));
                    }}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border
                      ${isSelected ? 'bg-indigo-700 dark:bg-indigo-500 border-indigo-700 dark:border-indigo-500' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-indigo-400'}
                      ${isSelected ? '!text-white' : 'text-slate-700 dark:text-slate-200'}
                    `}
                  >
                    {opt}
                  </button>
                );
              })}
              <button
                onClick={() => {
                  setCustomInputOpen(prev => ({ ...prev, [qi]: true }));
                  setSelections(s => ({ ...s, [qi]: '' }));
                }}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all border
                  ${isCustom ? 'bg-indigo-700 dark:bg-indigo-500 border-indigo-700 dark:border-indigo-500' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-indigo-400'}
                  ${isCustom ? '!text-white' : 'text-slate-700 dark:text-slate-200'}
                `}
              >
                自行輸入
              </button>
            </div>
            {isCustom && (
              <input
                autoFocus
                type="text"
                value={selections[qi] || ''}
                onChange={e => setSelections(s => ({ ...s, [qi]: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing && canSubmit) handleSubmit(); }}
                placeholder="請輸入自訂內容..."
                className="w-full bg-white dark:bg-slate-800 border border-indigo-400 dark:border-indigo-500 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            )}
          </div>
        );
      })}

      <button
        onClick={handleSubmit}
        disabled={!canSubmit || isTyping}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
          canSubmit && !isTyping
            ? 'bg-indigo-700 dark:bg-indigo-500 text-white hover:bg-indigo-600 dark:hover:bg-indigo-400'
            : 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
        }`}
      >
        送出 <ChevronRight size={12} />
      </button>
    </div>
  );
};
